import { useState } from 'react'
import { Button, Modal, Spinner, TextArea } from '@heroui/react'

import { errorMessage } from '@/lib/api/api-error'

import type { PosSession } from '../types'
import { branchLabel, chipStyle, relativeTime, sessionStatusStyles } from './pos-format'
import { ShiftReportPanel } from './shift-report-panel'
import { StatusChip } from './status-chip'

interface SessionDetailDialogProps {
  session: PosSession | null
  onOpenChange: (open: boolean) => void
  /**
   * Omit to open the drawer read-only.
   *
   * Absence is the signal, not a second flag: a caller with nothing to call has
   * no force-close to offer, and the Reports module is exactly that caller —
   * ending a trading period is part of a session's lifecycle, which lives in
   * Point of Sale, not on a screen for reading what was counted.
   */
  onForceClose?: (id: string, reason: string) => Promise<unknown>
  isClosing?: boolean
  error?: unknown
  /** Force-closing also needs `full` on the POS module; reading a report does not. */
  canForceClose?: boolean
}

/**
 * One drawer, arrived at from the session list.
 *
 * The reports themselves are `ShiftReportPanel`, shared with the Reports tab —
 * this dialog adds only what the list route needs around them: the row's own
 * status, and the force-close, which is the one write the back office has against
 * a session.
 */
export function SessionDetailDialog({
  session,
  onOpenChange,
  onForceClose,
  isClosing = false,
  error,
  canForceClose = true,
}: SessionDetailDialogProps) {
  const [reason, setReason] = useState('')
  const [isConfirming, setIsConfirming] = useState(false)

  const canEnd = canForceClose && Boolean(onForceClose)

  async function confirm() {
    if (!session || !onForceClose || !reason.trim()) return

    await onForceClose(session.id, reason.trim())
    setReason('')
    setIsConfirming(false)
    onOpenChange(false)
  }

  return (
    <Modal isOpen={Boolean(session)} onOpenChange={onOpenChange}>
      <Modal.Backdrop className="overlay-scope" variant="blur">
        <Modal.Container size="lg" scroll="inside" placement="center">
          <Modal.Dialog>
            <Modal.CloseTrigger className="end-3 top-3 size-10" />

            <Modal.Header className="gap-1 pe-10">
              <Modal.Heading>{session?.number}</Modal.Heading>
              <p className="text-muted text-xs">
                {session?.registerCode} · {branchLabel(session?.branch ?? null)}
              </p>
            </Modal.Header>

            <Modal.Body className="flex flex-col gap-5">
              {session && (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusChip style={chipStyle(sessionStatusStyles, session.status)} />
                    <span className="text-muted text-xs">
                      Opened {relativeTime(session.openedAt)}
                      {session.openedByName && ` by ${session.openedByName}`}
                    </span>
                  </div>

                  {session.status === 'force_closed' && (
                    <p className="text-muted border-border rounded-xl border border-dashed px-3 py-2 text-xs">
                      Force-closed from the back office. Nobody counted this drawer, so it carries no
                      variance — a zero there would claim a reconciliation that never happened, and
                      there is no Z to read.
                      {session.closedNote && (
                        <>
                          {' '}
                          <span className="text-foreground">{session.closedNote}</span>
                        </>
                      )}
                    </p>
                  )}

                  {/* Keyed by the drawer. The dialog stays mounted between rows,
                      and remounting the reports is what drops the previous
                      session's fetch state instead of showing it under a new
                      heading for a frame. */}
                  <ShiftReportPanel key={session.id} session={session} />

                  {canEnd && session.isOpen && (
                    <section className="border-border flex flex-col gap-2 border-t pt-4">
                      {isConfirming ? (
                        <>
                          <label className="flex flex-col gap-1.5">
                            <span className="text-sm font-medium">Why are you closing it?</span>
                            <TextArea
                              value={reason}
                              onChange={(event) => setReason(event.target.value)}
                              placeholder="Tablet died mid-service; drawer counted by hand."
                              maxLength={500}
                              className="w-full"
                            />
                          </label>
                          <p className="text-muted text-xs">
                            This ends a period a device may still be holding. It is recorded as
                            force-closed with no variance and no Z, and the reason is kept in the
                            audit trail.
                          </p>
                        </>
                      ) : (
                        <p className="text-muted text-xs">
                          Use this only for a drawer no device will ever close.
                        </p>
                      )}

                      {error != null && (
                        <p role="alert" className="text-danger text-xs">
                          {errorMessage(error, 'Could not close that session.')}
                        </p>
                      )}
                    </section>
                  )}
                </>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button variant="ghost" onPress={() => onOpenChange(false)} isDisabled={isClosing}>
                Close
              </Button>

              {canEnd &&
                session?.isOpen &&
                (isConfirming ? (
                  <Button
                    variant="primary"
                    isDisabled={isClosing || !reason.trim()}
                    onPress={() => void confirm()}
                  >
                    {isClosing && <Spinner size="sm" />}
                    {isClosing ? 'Closing…' : 'Force-close'}
                  </Button>
                ) : (
                  <Button variant="primary" onPress={() => setIsConfirming(true)}>
                    Force-close session
                  </Button>
                ))}
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  )
}
